import PropTypes from 'prop-types'
import { useCallback, useEffect, useRef, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import {
  Box,
  Collapse,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import CampaignIcon from '@mui/icons-material/Campaign'
import AssignmentIcon from '@mui/icons-material/Assignment'
import { ROUTE_METADATA } from 'app/AppRoutes/RouteMetadata'

export const DEFAULT_SIDEBAR_WIDTH = 240
export const MIN_SIDEBAR_WIDTH = 180
export const MAX_SIDEBAR_WIDTH = 400

const OUTREACH_ITEMS = [
  { label: 'Dashboard', path: ROUTE_METADATA.adminOutreachDashboard.path },
  { label: 'Tags', path: ROUTE_METADATA.adminOutreachTags.path },
  { label: 'Templates', path: ROUTE_METADATA.adminOutreachTemplates.path },
  { label: 'Campaigns', path: ROUTE_METADATA.adminOutreachCampaigns.path },
  {
    label: 'Highlight Cards',
    path: ROUTE_METADATA.adminOutreachHighlightCards.path,
  },
  {
    label: 'Push Notifications',
    path: ROUTE_METADATA.adminOutreachPushNotifications.path,
  },
]

const linkSx = {
  textDecoration: 'none',
  color: 'inherit',
  display: 'block',
}

const NavSection = ({ label, icon, items, onNavigate }) => {
  const { pathname } = useLocation()
  const basePath = items[0]?.path?.split('/').slice(0, 3).join('/') || ''
  const isActive = pathname.startsWith(basePath)
  const [open, setOpen] = useState(isActive)

  return (
    <>
      <ListItemButton
        onClick={() => setOpen((prev) => !prev)}
        sx={{
          '&&': { px: 2, py: 1.25 },
          width: '100%',
        }}
      >
        <ListItemIcon sx={{ minWidth: 36 }}>{icon}</ListItemIcon>
        <ListItemText
          primary={label}
          primaryTypographyProps={{ fontWeight: 600, fontSize: 14 }}
        />
        {open ? <ExpandLess /> : <ExpandMore />}
      </ListItemButton>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          {items.map((item) => {
            const isItemActive = pathname.startsWith(item.path)
            return (
              <Box
                key={item.path}
                component={NavLink}
                to={item.path}
                onClick={onNavigate}
                sx={linkSx}
              >
                <ListItemButton
                  selected={isItemActive}
                  sx={{
                    '&&': { pl: 5, pr: 2, py: 1 },
                    width: '100%',
                  }}
                >
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{ fontSize: 13 }}
                  />
                </ListItemButton>
              </Box>
            )
          })}
        </List>
      </Collapse>
    </>
  )
}

NavSection.propTypes = {
  label: PropTypes.string.isRequired,
  icon: PropTypes.node.isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      path: PropTypes.string.isRequired,
    }),
  ).isRequired,
  onNavigate: PropTypes.func,
}

const SidebarContent = ({ onNavigate }) => {
  const { pathname } = useLocation()
  const isComplianceActive = pathname.startsWith(
    ROUTE_METADATA.adminCompliance.path,
  )

  return (
    <List component="nav" disablePadding sx={{ pt: 1 }}>
      <NavSection
        label="Outreach"
        icon={<CampaignIcon fontSize="small" />}
        items={OUTREACH_ITEMS}
        onNavigate={onNavigate}
      />
      <Box
        component={NavLink}
        to={ROUTE_METADATA.adminCompliance.path}
        onClick={onNavigate}
        sx={{ ...linkSx, width: '100%' }}
      >
        <ListItemButton
          selected={isComplianceActive}
          sx={{ '&&': { px: 2, py: 1.25 }, width: '100%' }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <AssignmentIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Compliance"
            primaryTypographyProps={{ fontWeight: 600, fontSize: 14 }}
          />
        </ListItemButton>
      </Box>
    </List>
  )
}

const ResizeHandle = ({ onMouseDown }) => (
  <Box
    onMouseDown={onMouseDown}
    sx={{
      position: 'absolute',
      top: 0,
      right: 0,
      bottom: 0,
      width: 4,
      cursor: 'col-resize',
      '&:hover': { backgroundColor: 'primary.main', opacity: 0.3 },
      zIndex: 1,
    }}
  />
)

const AdminSidebar = ({
  mobileOpen,
  onMobileClose,
  collapsed,
  onToggleCollapse,
  width,
  onWidthChange,
}) => {
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const dragging = useRef(false)
  const listenersRef = useRef({ move: null, up: null })

  useEffect(
    () => () => {
      if (listenersRef.current.move) {
        document.removeEventListener('mousemove', listenersRef.current.move)
      }
      if (listenersRef.current.up) {
        document.removeEventListener('mouseup', listenersRef.current.up)
      }
    },
    [],
  )

  const handleMouseDown = useCallback(
    (e) => {
      e.preventDefault()
      dragging.current = true

      const handleMouseMove = (moveEvent) => {
        if (!dragging.current) return
        const newWidth = Math.min(
          MAX_SIDEBAR_WIDTH,
          Math.max(MIN_SIDEBAR_WIDTH, moveEvent.clientX),
        )
        onWidthChange(newWidth)
      }

      const handleMouseUp = () => {
        dragging.current = false
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
        listenersRef.current = { move: null, up: null }
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
      }

      listenersRef.current = { move: handleMouseMove, up: handleMouseUp }
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    },
    [onWidthChange],
  )

  if (!isDesktop) {
    return (
      <Drawer
        variant="temporary"
        anchor="left"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          '& .MuiDrawer-paper': {
            width: DEFAULT_SIDEBAR_WIDTH,
          },
        }}
      >
        <SidebarContent onNavigate={onMobileClose} />
      </Drawer>
    )
  }

  if (collapsed) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          borderRight: 1,
          borderColor: 'divider',
          height: '100%',
        }}
      >
        <Box sx={{ flex: 1 }} />
        <Box sx={{ py: 1 }}>
          <Tooltip title="Expand sidebar" placement="right">
            <IconButton
              onClick={onToggleCollapse}
              size="small"
              aria-label="Expand sidebar"
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    )
  }

  return (
    <Box sx={{ position: 'relative', width, flexShrink: 0 }}>
      <Drawer
        variant="permanent"
        anchor="left"
        sx={{
          width: '100%',
          height: '100%',
          '& .MuiDrawer-paper': {
            width: '100%',
            position: 'static',
            borderRight: 'none',
            height: '100%',
            overflow: 'hidden auto',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <SidebarContent onNavigate={() => {}} />
        <Box sx={{ flex: 1 }} />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            px: 0.5,
            py: 1,
          }}
        >
          <Tooltip title="Collapse sidebar">
            <IconButton
              onClick={onToggleCollapse}
              size="small"
              aria-label="Collapse sidebar"
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Drawer>
      <ResizeHandle onMouseDown={handleMouseDown} />
    </Box>
  )
}

AdminSidebar.propTypes = {
  mobileOpen: PropTypes.bool,
  onMobileClose: PropTypes.func,
  collapsed: PropTypes.bool,
  onToggleCollapse: PropTypes.func,
  width: PropTypes.number,
  onWidthChange: PropTypes.func,
}

export default AdminSidebar
